import { Route } from '@helpers/types';
// @ts-ignore
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import * as turf from '@turf/turf';
import { Feature, Polygon, Position } from 'geojson';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MapRef } from 'react-map-gl/maplibre';

export const useDeckDrawer = (
  mapRef: React.RefObject<MapRef | null>,
  routes: Array<Route> | undefined,
  onMoveOrdersToRoute: (
    trackings: Array<number>,
    route_id: string,
  ) => Promise<void>,
) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const drawRef = useRef<MapboxDraw | null>(null);
  const [controlAdded, setControlAdded] = useState(false);
  const [trackings, setTrackings] = useState<Array<number>>([]);
  const [route, setRoute] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const startSelection = useCallback(() => {
    if (drawRef.current) {
      drawRef.current.changeMode('draw_polygon');
      setIsEditMode(true);
    }
  }, []);

  const cancelSelection = useCallback(() => {
    if (drawRef.current) {
      drawRef.current.deleteAll();
      setTrackings([]);
      setIsEditMode(false);
    }
  }, []);

  const onDrawCreate = useCallback(
    (e: { features: Feature<Polygon>[] }) => {
      if (!routes || !e.features.length) return;
      
      const polygon = e.features[0].geometry;
      
      const trackings = routes
        .map((route) => {
          return route.orders
            .filter((order) => {
              const pt = turf.point(order.geometry.coordinates as Position);
              // @ts-ignore
              return turf.booleanPointInPolygon(pt, polygon);
            })
            .map((order) => order.tracking_id);
        })
        .flat();

      if (drawRef.current) {
        drawRef.current.deleteAll();
        setIsEditMode(false);
      }
      setTrackings(trackings);
    },
    [routes],
  );

  const onChangeRoute = useCallback((route_id: string) => {
    setRoute(route_id);
  }, []);

  const onAccept = useCallback(() => {
    setIsLoading(true);
    onMoveOrdersToRoute(trackings, route)
      .then(() => {
        setTrackings([]);
        setRoute('');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [trackings, route, onMoveOrdersToRoute]);

  const onReject = useCallback(() => {
    setTrackings([]);
    setRoute('');
  }, []);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || controlAdded) return;

    const draw = new MapboxDraw({
      keybindings: false,
      displayControlsDefault: false,
      controls: {
        polygon: true,
        trash: true,
      },
      // @ts-ignore
      modes: MapboxDraw.modes // Ensure default modes are loaded
    });
    
    drawRef.current = draw;
    // @ts-ignore
    map.addControl(draw);
    setControlAdded(true);

    return () => {
        // Cleanup if needed, though usually map removal handles it
    };
  }, [mapRef, controlAdded]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !controlAdded) return;

    map.on('draw.create', onDrawCreate);
    return () => {
      map.off('draw.create', onDrawCreate);
    };
  }, [mapRef, controlAdded, onDrawCreate]);

  return {
    route,
    isLoading,
    onChangeRoute,
    onAccept,
    onReject,
    trackings,
    isEditMode,
    startSelection,
    cancelSelection,
  };
};